import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../data/supabaseClient';
import { CustomInputBox1, CustomButton2, CustomButton10Ca } from '../Templates';
import Spinner from '../common/Spinner';
import { FEATURES } from '../../config/features';
import { 
  detectUserCountry, 
  getCountryOptions, 
  validateCountryChange, 
  formatCountryDisplay, 
  getUserCountry 
} from '../../utils/countryUtils';
import './ProfileComplete.css';

// Import default avatars - keep your existing imports
import Favatar1 from '../Media/avatars/Favatar1.jpg';
import Favatar2 from '../Media/avatars/Favatar2.jpg';
import Favatar3 from '../Media/avatars/Favatar3.jpg';
import Mavatar1 from '../Media/avatars/Mavatar1.jpg';
import Mavatar2 from '../Media/avatars/Mavatar2.jpg';
import Mavatar3 from '../Media/avatars/Mavatar3.jpg';
import Ravatar1 from '../Media/avatars/Ravatar1.jpg';

// European countries with their codes
const EUROPEAN_COUNTRIES = [
  { code: '+371', country: 'LV', name: 'Latvia' },
  { code: '+370', country: 'LT', name: 'Lithuania' },
  { code: '+372', country: 'EE', name: 'Estonia' },
  { code: '+358', country: 'FI', name: 'Finland' },
  { code: '+46', country: 'SE', name: 'Sweden' },
  { code: '+47', country: 'NO', name: 'Norway' },
  { code: '+45', country: 'DK', name: 'Denmark' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+353', country: 'IE', name: 'Ireland' },
  { code: '+31', country: 'NL', name: 'Netherlands' },
  { code: '+32', country: 'BE', name: 'Belgium' },
  { code: '+41', country: 'CH', name: 'Switzerland' },
  { code: '+43', country: 'AT', name: 'Austria' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+351', country: 'PT', name: 'Portugal' },
  { code: '+48', country: 'PL', name: 'Poland' },
  { code: '+420', country: 'CZ', name: 'Czech Republic' },
  { code: '+421', country: 'SK', name: 'Slovakia' },
  { code: '+36', country: 'HU', name: 'Hungary' },
  { code: '+386', country: 'SI', name: 'Slovenia' },
  { code: '+385', country: 'HR', name: 'Croatia' },
  { code: '+387', country: 'BA', name: 'Bosnia and Herzegovina' },
  { code: '+381', country: 'RS', name: 'Serbia' },
  { code: '+382', country: 'ME', name: 'Montenegro' },
  { code: '+383', country: 'XK', name: 'Kosovo' },
  { code: '+389', country: 'MK', name: 'North Macedonia' },
  { code: '+359', country: 'BG', name: 'Bulgaria' },
  { code: '+40', country: 'RO', name: 'Romania' },
  { code: '+30', country: 'GR', name: 'Greece' },
  { code: '+90', country: 'TR', name: 'Turkey' },
  { code: '+7', country: 'RU', name: 'Russia' },
  { code: '+375', country: 'BY', name: 'Belarus' },
  { code: '+380', country: 'UA', name: 'Ukraine' },
  { code: '+373', country: 'MD', name: 'Moldova' }
];

const DEFAULT_COUNTRY = EUROPEAN_COUNTRIES[0]; // Latvia as default

// Validation constants
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 50;
const NAME_REGEX = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0400-\u04FF\s'-]+$/; // Supports Latin, accented, Cyrillic
const PHONE_REGEX = /^\d{6,12}$/; // 6-12 digits (without country code)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const DEFAULT_AVATARS = [
  { id: 1, url: Favatar1 },
  { id: 2, url: Favatar2 },
  { id: 3, url: Favatar3 },
  { id: 4, url: Mavatar1 },
  { id: 5, url: Mavatar2 },
  { id: 6, url: Mavatar3 },
  { id: 7, url: Ravatar1 }
];

const Step2PersonalInfo = ({ userData, onComplete }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    year_of_birth: '',
    gender: '', // Default to empty - let user choose
    phone: '',
    avatar_url: null
    // country_code removed - now in Step 1
  });
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(DEFAULT_COUNTRY);
  // Country state removed - now handled in Step 1
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [showDefaultAvatars, setShowDefaultAvatars] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Validation functions
  const validateName = (name, fieldName, isRequired = true) => {
    if (!name || name.trim().length === 0) {
      return isRequired ? t('errors.field_required', { field: fieldName }) : null;
    }
    
    const trimmedName = name.trim();
    
    if (trimmedName.length < NAME_MIN_LENGTH) {
      return t('errors.name_too_short', { field: fieldName, min: NAME_MIN_LENGTH });
    }
    
    if (trimmedName.length > NAME_MAX_LENGTH) {
      return t('errors.name_too_long', { field: fieldName, max: NAME_MAX_LENGTH });
    }
    
    if (!NAME_REGEX.test(trimmedName)) {
      return t('errors.name_invalid_characters', { field: fieldName });
    }
    
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone || phone.trim().length === 0) {
      return null; // Phone is optional
    }
    
    const cleanPhone = phone.replace(/[\s()-]/g, ''); // Remove common formatting
    
    if (!PHONE_REGEX.test(cleanPhone)) {
      return t('errors.phone_invalid_format');
    }
    
    return null;
  };

  const validateFile = (file) => {
    if (!file) return null;
    
    // Check file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return t('errors.invalid_file_type');
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('errors.file_too_large', { maxSize: '5MB' });
    }
    
    return null;
  };

  // Load initial data
  useEffect(() => {
    if (userData?.profile) {
      setFormData(prev => ({
        ...prev,
        first_name: userData.profile.first_name || '',
        last_name: userData.profile.last_name || '',
        year_of_birth: userData.profile.year_of_birth || '',
        gender: userData.profile.gender || '', // Don't default to male
        phone: userData.profile.phone || '',
        avatar_url: userData.profile.avatar_url || null
        // country_code removed - now in Step 1
      }));
      setPreviewUrl(userData.profile.avatar_url);
    }
  }, [userData]);

  const handleImageUpload = async (event) => {
    try {
      setUploadingImage(true);
      setError(null);
      setValidationErrors(prev => ({ ...prev, avatar: null }));

      const file = event.target.files[0];
      if (!file) return;

      // Validate file
      const fileValidationError = validateFile(file);
      if (fileValidationError) {
        setValidationErrors(prev => ({ ...prev, avatar: fileValidationError }));
        return;
      }

      // Show immediate preview
      const localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);

      // Generate unique filename with proper extension
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `avatars/${userData.id}/${fileName}`;

      // Upload image to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      console.log('Upload successful, public URL:', publicUrl);

      setFormData(prev => ({
        ...prev,
        avatar_url: publicUrl
      }));

      // Clean up the local preview URL
      if (localPreviewUrl && localPreviewUrl !== publicUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      
      setPreviewUrl(publicUrl);

    } catch (error) {
      console.error('Error uploading image:', error);
      setValidationErrors(prev => ({ 
        ...prev, 
        avatar: error.message || t('errors.upload_failed') 
      }));
      // Don't clear preview on error so user can try again
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDefaultAvatarSelect = (avatarUrl) => {
    setFormData(prev => ({
      ...prev,
      avatar_url: avatarUrl
    }));
    setPreviewUrl(avatarUrl);
    setShowDefaultAvatars(false);
    setValidationErrors(prev => ({ ...prev, avatar: null }));
  };

  // Input change handlers with validation
  const handleInputChange = (field, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear previous validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [field]: null
    }));

    // Validate on change for immediate feedback
    let validationError = null;
    
    switch (field) {
      case 'first_name':
        validationError = validateName(value, t('profile_setup.first_name'), true);
        break;
      case 'last_name':
        validationError = validateName(value, t('profile_setup.last_name'), true);
        break;
      case 'phone':
        validationError = validatePhone(value);
        break;
      default:
        break;
    }

    if (validationError) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationError
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Validate required fields (first_name and last_name are now mandatory)
      const errors = {};
      
      // First name is required
      const firstNameError = validateName(formData.first_name, t('profile_setup.first_name'), true);
      if (firstNameError) errors.first_name = firstNameError;
      
      // Last name is required
      const lastNameError = validateName(formData.last_name, t('profile_setup.last_name'), true);
      if (lastNameError) errors.last_name = lastNameError;
      
      // Validate phone only if provided
      if (formData.phone && formData.phone.trim()) {
        const phoneError = validatePhone(formData.phone);
        if (phoneError) errors.phone = phoneError;
      }
      
      // Validate year of birth only if provided
      if (formData.year_of_birth) {
        const currentYear = new Date().getFullYear();
        const birthYear = parseInt(formData.year_of_birth);
        if (birthYear < 1900 || birthYear > currentYear - 13) {
          errors.year_of_birth = t('errors.invalid_birth_year');
        }
      }

      // If there are validation errors, show them and stop
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setError(t('errors.please_fix_errors'));
        return;
      }

      // Sanitize and prepare data for submission
      const sanitizedData = {
        // First name and last name are required
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim()
      };
      
      if (formData.year_of_birth) {
        sanitizedData.year_of_birth = parseInt(formData.year_of_birth);
      }
      
      // Always include gender (has default or user choice)
      sanitizedData.gender = formData.gender || null; // Store null if not selected
      
      if (formData.phone && formData.phone.trim()) {
        // Combine country code with phone number
        const cleanPhone = formData.phone.replace(/[\s()-]/g, '');
        sanitizedData.phone = selectedPhoneCountry.code + cleanPhone;
      }
      
      if (formData.avatar_url) {
        sanitizedData.avatar_url = formData.avatar_url;
      }

      // Include country code from Step 1 (passed through userData)
      if (FEATURES.MULTI_COUNTRY_ENABLED && userData?.step1Data?.country_code) {
        sanitizedData.country_code = userData.step1Data.country_code;
        console.log('🌍 Using country from Step 1:', userData.step1Data.country_code);
      }

      console.log('Step 2 data to save:', sanitizedData);

      // Complete the step
      await onComplete(sanitizedData);
    } catch (error) {
      console.error('Error saving personal info:', error);
      setError(error.message || t('errors.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i - 16);

  return (
    <form onSubmit={handleSubmit} className="pcf-form-step">
      <div className="pcf-avatar-section">
        {previewUrl && (
          <img 
            src={previewUrl} 
            alt={t('profile_setup.profile_picture')}
            className="pcf-avatar-preview"
          />
        )}
        
        <div className="pcf-avatar-options">
          <div className="pcf-avatar-upload">
            <input
              type="file"
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={handleImageUpload}
              id="avatar-upload"
              disabled={uploadingImage}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="enhanced-profile-button secondary"
              onClick={() => document.getElementById('avatar-upload').click()}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                <div className="button-label-with-spinner">
                  <Spinner size="small" color="var(--light-color)" showText={false} />
                  <span>{t('app.uploading')}</span>
                </div>
              ) : (
                t('buttons.upload')
              )}
            </button>
          </div>
        </div>
        
        {validationErrors.avatar && (
          <span className="pcf-error-text">{validationErrors.avatar}</span>
        )}
        
        <div className="pcf-default-avatars-grid">
          {DEFAULT_AVATARS.map((avatar) => (
            <div 
              key={avatar.id} 
              className="pcf-default-avatar-item"
              onClick={() => handleDefaultAvatarSelect(avatar.url)}
            >
              <img 
                src={avatar.url} 
                alt={`${t('profile_setup.avatar')} ${avatar.id}`}
                className="pcf-default-avatar-preview"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="pcf-content-container">
        <div className="pcf-input-group">
          <div className="pcf-form-field">
            <label>{t('profile_setup.first_name')} <span className="pcf-required">*</span></label>
            <input
              className="enhanced-profile-input"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder={t('profile_setup.first_name')}
              disabled={loading}
              maxLength={NAME_MAX_LENGTH}
              required={true}
            />
            {validationErrors.first_name && (
              <span className="pcf-error-text">{validationErrors.first_name}</span>
            )}
          </div>

          <div className="pcf-form-field">
            <label>{t('profile_setup.last_name')} <span className="pcf-required">*</span></label>
            <input
              className="enhanced-profile-input"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder={t('profile_setup.last_name')}
              disabled={loading}
              maxLength={NAME_MAX_LENGTH}
              required={true}
            />
            {validationErrors.last_name && (
              <span className="pcf-error-text">{validationErrors.last_name}</span>
            )}
          </div>

          <div className="pcf-form-field">
            <label>{t('profile_setup.birth_year')}</label>
            <select
              id="year-select"
              className="pcf-form-select"
              value={formData.year_of_birth}
              onChange={(e) => handleInputChange('year_of_birth', e.target.value)}
              disabled={loading}
              aria-label={t('profile_setup.birth_year')}
            >
              <option value="">{t('profile_setup.select_year')}</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {validationErrors.year_of_birth && (
              <span className="pcf-error-text">{validationErrors.year_of_birth}</span>
            )}
          </div>

          <div className="pcf-form-field">
            <label>{t('profile_setup.gender')}</label>
            <select
              id="gender-select"
              className="pcf-form-select"
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              disabled={loading}
              aria-label={t('profile_setup.gender')}
            >
              <option value="">{t('profile_setup.select_gender')}</option>
              <option value="male">{t('profile_setup.male')}</option>
              <option value="female">{t('profile_setup.female')}</option>
              <option value="prefer_not_to_say">{t('profile_setup.prefer_not_to_say')}</option>
            </select>
            {validationErrors.gender && (
              <span className="pcf-error-text">{validationErrors.gender}</span>
            )}
          </div>

          <div className="pcf-input-wrapper">
            <label className="pcf-phone-label">{t('profile_setup.phone')}</label>
            <div className="pcf-phone-input-container">
              <select 
                className="pcf-country-select"
                value={selectedPhoneCountry.code}
                onChange={(e) => {
                  const country = EUROPEAN_COUNTRIES.find(c => c.code === e.target.value);
                  setSelectedPhoneCountry(country);
                }}
                disabled={loading}
              >
                {EUROPEAN_COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.country} {country.code}
                  </option>
                ))}
              </select>
              <input
                className="enhanced-profile-input"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="12345678"
                type="tel"
                disabled={loading}
                maxLength="12"
                required={false}
              />
            </div>
            {validationErrors.phone && (
              <span className="pcf-error-text">{validationErrors.phone}</span>
            )}
            <small className="pcf-helper-text">
              {t('profile_setup.phone_format_help')}
            </small>
          </div>
        </div>

        {error && <div className="pcf-error-text">{error}</div>}

        <div className="pcf-button-group">
          <button
            type="submit"
            className="enhanced-profile-button primary"
            disabled={loading}
          >
            {loading ? (
              <div className="button-label-with-spinner">
                <Spinner size="small" color="var(--background-color)" showText={false} />
                <span>{t('app.loading')}</span>
              </div>
            ) : (
              t('buttons.next')
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step2PersonalInfo;